/**
 * The one router.
 *
 * Every route in the platform is declared here and renders inside the single
 * application shell. There is no second shell behind any path — moving from
 * accounting to HR is a route change, not an application change.
 *
 * Routes are added as their module lands. Nothing is registered that cannot
 * actually be opened, which is the same rule the sidebar follows.
 */

import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/modules/auth/LoginPage';
import { Spinner, EmptyState } from '@/components/shared/states';
import { RequirePermission } from '@/components/shared/RequirePermission';
import { useSession } from '@/stores/session';
import { accountingRoutes } from '@/modules/accounting/routes';
import { hrRoutes } from '@/modules/hr/routes';
import { operationsRoutes } from '@/modules/operations/routes';
import { DispatchPrintPage } from '@/modules/operations/dispatch/DispatchPrint';
import { FieldOrderPrintPage } from '@/modules/field/areas/commerce/FieldOrderPrintPage';
import { fieldRoutes } from '@/modules/field/routes';
import { platformRoutes } from '@/modules/platform/routes';
import { doRoutes } from '@/modules/do/routes';

const DashboardPage = lazy(() =>
  import('@/modules/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));

/*
 * The home screen is no longer the dashboard.
 *
 * What lands at / is now the list of things this person can actually do,
 * in their own words. The dashboard figures have not gone anywhere: they
 * are at /dashboard, offered as "Business overview" to whoever may see them.
 */
const HomePage = lazy(() =>
  import('@/modules/do/HomePage').then((m) => ({ default: m.HomePage })));

/*
 * A distributor's or a retailer's login opens their own portal: a small,
 * phone-first application of its own, outside the ERP shell entirely.
 */
const PortalApp = lazy(() =>
  import('@/modules/portal/PortalApp').then((m) => ({ default: m.PortalApp })));

function Loading() {
  return <Spinner label="Loading…" />;
}

function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/** Signed in, or sent to the login page with somewhere to come back to. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const ready = useSession((s) => s.ready);
  const user = useSession((s) => s.user);
  const portal = useSession((s) => s.portal);
  const location = useLocation();

  if (!ready) return <FullScreenLoading />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (portal) return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

/** The portal is for portal logins only; everybody else goes to the ERP. */
function RequirePortal({ children }: { children: React.ReactNode }) {
  const ready = useSession((s) => s.ready);
  const user = useSession((s) => s.user);
  const portal = useSession((s) => s.portal);
  const location = useLocation();

  if (!ready) return <FullScreenLoading />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (!portal) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export { RequirePermission } from '@/components/shared/RequirePermission';

function FullScreenLoading() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <div className="text-center">
        <span
          aria-hidden
          className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-primary text-[17px] font-bold text-primary-foreground"
        >
          R
        </span>
        <Spinner label="Starting RSC ERP…" />
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <EmptyState
      title="Screen not found"
      message="That address does not match any screen in the application."
    />
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/*" element={<RequirePortal><Page><PortalApp /></Page></RequirePortal>} />

      {/* Printed documents fill the page, so they sit outside the shell.
          /print/voucher/:id used to sit here. It asked the server for
          /api/communication/print/voucher/:id, which has not existed since
          the accounting routes were removed, so the page could only ever
          show a failure. Field orders and dispatch challans print from
          their own screens below, and those work. */}
      <Route
        path="/print/dispatch/:id"
        element={<RequireAuth><Page><RequirePermission module="erp.dispatch" action="print" what="dispatch challans"><DispatchPrintPage /></RequirePermission></Page></RequireAuth>}
      />
      <Route
        path="/print/field-order/:id"
        element={<RequireAuth><Page><RequirePermission module="field.order" action="print" what="field order summaries"><FieldOrderPrintPage /></RequirePermission></Page></RequireAuth>}
      />

      <Route
        element={(
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        )}
      >
        <Route index element={<Page><HomePage /></Page>} />
        <Route
          path="dashboard"
          element={(
            <Page>
              <RequirePermission module="dashboard" what="the business overview">
                <DashboardPage />
              </RequirePermission>
            </Page>
          )}
        />

        {doRoutes()}
        {accountingRoutes()}
        {hrRoutes()}
        {operationsRoutes()}
        {fieldRoutes()}
        {platformRoutes()}

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
